import React, { useMemo } from "react";
import { Autocomplete, Box, createFilterOptions } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";

// Searchable variant of the filter-bar Filter (see ListHeader.tsx). Used for
// large option sets where a plain Dropdown is unusable (e.g. ~27k styles).
// Same contract as Filter: selections flow into ListHeader's uncommitted
// filters via onOptionSelect and only hit the query on "Apply Filters".
// Modeled on the MUI Autocomplete precedent in
// presentation/components/autocompletes/manufacturer-auto.complete.tsx.
interface AutocompleteFilterProps<T> {
  filterKey: string;
  options: T[];
  initialValue?: string | number | boolean;
  onOptionSelect: (event: {
    value: string | number | boolean | undefined;
    key: string;
  }) => void;
  getOptionValue: (option: T) => string | number;
  getOptionDisplay?: (option: T) => string;
  defaultTitle: string;
  // Optional narrowing by another (uncommitted) filter's value - options are
  // narrowed while it has a value, searching all options otherwise.
  narrowValue?: string | number | boolean;
  narrowOption?: (option: T, narrowValue: string | number | boolean) => boolean;
  // Hint shown while the narrowing filter has no value.
  narrowHint?: string;
}

export function AutocompleteFilter<T>({
  filterKey,
  options,
  initialValue,
  onOptionSelect,
  getOptionValue,
  getOptionDisplay,
  defaultTitle,
  narrowValue,
  narrowOption,
  narrowHint,
}: AutocompleteFilterProps<T>) {
  const getDisplay =
    getOptionDisplay ?? ((datum: T) => "" + getOptionValue(datum));

  // Option sets can be large (~27k styles): precompute labels and share one
  // collator so the one-time sort stays cheap.
  const sortedOptions = useMemo(() => {
    const collator = new Intl.Collator(undefined, { sensitivity: "base" });
    return options
      .map((option) => ({ option, label: getDisplay(option) }))
      .sort((a, b) => collator.compare(a.label, b.label))
      .map((x) => x.option);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  // Fully controlled (mirrors Filter): the selection lives in ListHeader's
  // uncommitted filter state, so "Reset filters" clears this too.
  const selected =
    sortedOptions.find((x) => "" + getOptionValue(x) === "" + initialValue) ??
    null;

  const baseFilterOptions = createFilterOptions<T>({
    limit: 100,
    stringify: getDisplay,
  });

  return (
    <div className="w-48 !text-xs">
      <Autocomplete
        className={"!text-xs"}
        disablePortal
        options={sortedOptions}
        value={selected}
        // Narrowing happens here (not on `options`) so a selection made
        // before narrowing stays a valid Autocomplete value.
        filterOptions={(opts, state) =>
          baseFilterOptions(
            narrowOption && narrowValue !== undefined && narrowValue !== ""
              ? opts.filter((o) => narrowOption(o, narrowValue))
              : opts,
            state,
          )
        }
        getOptionLabel={getDisplay}
        isOptionEqualToValue={(option, value) =>
          !!value && getOptionValue(option) === getOptionValue(value)
        }
        renderOption={(props, option) => {
          // @ts-ignore
          const { key, ...otherProps } = props;
          return (
            <Box key={key} component="li" {...otherProps}>
              <div className={"flex text-xs items-center"}>
                <p>{getDisplay(option)}</p>
              </div>
            </Box>
          );
        }}
        renderInput={(params) => (
          <div
            className="text-xs border rounded-md border-solid border-gray-200 h-9 flex space-x-2 items-center pl-2 bg-white"
            ref={params.InputProps.ref}
          >
            <span>
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="text-gray-500 text-xs"
              />
            </span>
            <input
              placeholder={defaultTitle}
              {...params.inputProps}
              className="w-full border-none text-gray-600 text-xs font-medium outline-none"
            />
            {/* Keep MUI's clear button so an applied choice can be removed. */}
            {params.InputProps.endAdornment}
          </div>
        )}
        onChange={(_, v) =>
          onOptionSelect({
            value: v ? getOptionValue(v) : undefined,
            key: filterKey,
          })
        }
      />
      {narrowHint &&
        !selected &&
        (narrowValue === undefined || narrowValue === "") && (
          <p className="italic text-xxs text-gray-500 mt-0.5">{narrowHint}</p>
        )}
    </div>
  );
}
